import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Client-side debounce/throttle for expensive actions (Test / Sync buttons).
 *
 *   const { run, disabled, cooldownRemaining } = useDebouncedAction(handler, 3000);
 *
 * • The returned `run` invokes `handler` at most once per `cooldownMs`.
 * • Calls made during the cooldown are silently ignored (no-op).
 * • `disabled` and `cooldownRemaining` (in seconds, rounded up) drive UI feedback.
 */
export function useDebouncedAction(action, cooldownMs = 3000) {
  const lastAtRef = useRef(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (cooldownRemaining <= 0) return undefined;
    const id = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.ceil((lastAtRef.current + cooldownMs - Date.now()) / 1000)
      );
      setCooldownRemaining(remaining);
      if (remaining <= 0) clearInterval(id);
    }, 250);
    return () => clearInterval(id);
  }, [cooldownRemaining, cooldownMs]);

  const run = useCallback(
    async (...args) => {
      const now = Date.now();
      if (now - lastAtRef.current < cooldownMs) return undefined;
      lastAtRef.current = now;
      setCooldownRemaining(Math.ceil(cooldownMs / 1000));
      setRunning(true);
      try {
        return await action(...args);
      } finally {
        setRunning(false);
      }
    },
    [action, cooldownMs]
  );

  return {
    run,
    running,
    cooldownRemaining,
    disabled: running || cooldownRemaining > 0,
  };
}
